import type { ReactNode } from "react"

import { useCompany } from "~/hooks/useCompany"
import { formatCurrency, formatNumber } from "~/utils/formatting"
import { cn } from "~/utils/helpers"

import ChatUnavailableBlock from "./ChatUnavailableBlock"
import CopyableTable from "./CopyableTable"
import { renderInlineMarkdownLinks } from "./MarkdownLink"
import ProductImage from "./ProductImage"

type ColumnType = "string" | "number" | "date" | "currency" | "percent" | "image" | "rank"

interface TableColumn {
  key: string
  label: string
  type: ColumnType
  emphasis?: "primary" | "default"
}

interface TableBlockData {
  title?: string
  columns: TableColumn[]
  rows: Array<Record<string, unknown>>
  totalRow?: Record<string, unknown>
  notice?: string
  emphasis?: "top-list" | "compact" | "default"
}

interface ChatTableBlockProps {
  validated?: unknown
  compact?: boolean
  /** Hide the table's internal title (the surrounding container provides one). */
  bare?: boolean
}

function isImageCell(value: unknown): value is { url?: string | null; alt?: string } {
  return typeof value === "object" && value !== null && "url" in value
}

function formatCell(
  value: unknown,
  col: TableColumn,
  currency: string,
  rowIndex: number,
  _row: Record<string, unknown>,
  density: "comfortable" | "compact",
): ReactNode {
  const { type } = col

  if (type === "image") {
    const cell = isImageCell(value) ? value : { url: typeof value === "string" ? value : null }
    const url = cell.url ?? null
    const alt = cell.alt ?? "image"
    const w = density === "comfortable" ? 40 : 28
    return <ProductImage url={url} alt={alt} width={w} withHoverPreview />
  }

  if (type === "rank") {
    // Prefer an explicit rank value; otherwise fall back to 1-based row index.
    // rowIndex < 0 marks the totalRow path where ranks don't apply.
    if (typeof value !== "number" && rowIndex < 0) return ""
    const n = typeof value === "number" ? value : rowIndex + 1
    return (
      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-base px-1.5 label-sm text-fg-subtle">
        {n}
      </span>
    )
  }

  if (value == null) return ""
  if (type === "number" && typeof value === "number") {
    return formatNumber(value, 2)
  }
  if (type === "currency" && typeof value === "number") {
    return formatCurrency(value, currency)
  }
  if (type === "percent" && typeof value === "number") {
    return `${formatNumber(value, 1)}%`
  }
  if (type === "date") {
    if (typeof value === "string") return value
    if (value instanceof Date) return value.toISOString().slice(0, 10)
  }
  if (typeof value === "string") return renderInlineMarkdownLinks(value)
  return String(value)
}

function cellAlignClass(type: ColumnType): string {
  // Numerics hug the right edge. Image/rank/text stay left.
  if (type === "number" || type === "currency" || type === "percent") return "text-right"
  return "text-left"
}

function cellEmphasisClass(col: TableColumn, density: "comfortable" | "compact"): string {
  if (col.emphasis === "primary") {
    return density === "comfortable" ? "label-base text-fg" : "label-sm text-fg"
  }
  return ""
}

export default function ChatTableBlock({ validated, compact, bare }: ChatTableBlockProps) {
  const { data: company } = useCompany()
  const currency = company?.currency ?? "EUR"
  const data = validated as TableBlockData | undefined

  if (!data || !Array.isArray(data.columns) || !Array.isArray(data.rows)) {
    return (
      <pre className="overflow-x-auto rounded-lg bg-medium p-3 text-xs">
        <code>Invalid table block</code>
      </pre>
    )
  }

  const density: "comfortable" | "compact" =
    data.emphasis === "top-list"
      ? "comfortable"
      : compact || data.emphasis === "compact"
        ? "compact"
        : "comfortable"

  const rowPad = density === "comfortable" ? "px-2 py-2" : "px-2 py-1"
  const headerPad = "px-2 py-[5px]"

  return (
    <>
      {!bare && data.title && <div className="label-base mt-2">{data.title}</div>}
      <CopyableTable compact={density === "compact"}>
        {/* z-10 keeps the sticky header above row content — without it, ProductImage
            cells (relative + shadow) paint above the header when the user scrolls. */}
        <thead className="bg-medium text-fg-subtle sticky top-0 z-10">
          <tr>
            {data.columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  "label-base align-middle first:rounded-l-[8px] last:rounded-r-[8px] whitespace-nowrap",
                  headerPad,
                  cellAlignClass(c.type),
                  // Image / rank headers are usually decoration — render the label but keep it tight.
                  (c.type === "image" || c.type === "rank") && "w-px",
                )}
              >
                {c.type === "rank" ? "" : c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i} className="hover:bg-base-hover transition-colors">
              {data.columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    "align-middle first:rounded-l-[8px] last:rounded-r-[8px] whitespace-nowrap",
                    rowPad,
                    cellAlignClass(c.type),
                    cellEmphasisClass(c, density),
                  )}
                >
                  {formatCell(row[c.key], c, currency, i, row, density)}
                </td>
              ))}
            </tr>
          ))}
          {data.totalRow && (
            <tr className="hover:bg-base-hover transition-colors label-sm">
              {data.columns.map((c, i) => (
                <td
                  key={c.key}
                  className={cn(
                    "align-middle first:rounded-l-[8px] last:rounded-r-[8px] whitespace-nowrap",
                    rowPad,
                    cellAlignClass(c.type),
                  )}
                >
                  {i === 0 && !(c.key in data.totalRow!)
                    ? "Total"
                    : formatCell(data.totalRow![c.key], c, currency, -1, data.totalRow!, density)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </CopyableTable>
      {data.notice && <ChatUnavailableBlock validated={{ reason: data.notice }} />}
    </>
  )
}
